import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MessageCircle, Loader2 } from "lucide-react";
import { Reveal, Overline } from "./primitives";
import { OtpField } from "./OtpField";
import { CompanyInput } from "../SmartFields";
import { isValidEmail } from "../../lib/validate";
import { SERVICES, OTHER_SERVICES, EMAIL, PHONE, WHATSAPP_LINK } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
  const [otpToken, setOtpToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!isValidEmail(form.email)) { toast.error("Enter a valid email, e.g. abc@gmail.com or you@company.com"); return; }
    if (!otpToken) { toast.error("Please verify your email with the OTP first"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, otp_token: otpToken });
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", interest: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 py-4 font-body text-gf-text placeholder:text-gf-muted/60 focus:outline-none focus:border-gf-primary transition-colors duration-200";

  return (
    <section id="contact" className="relative z-10 pt-24 md:pt-36 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-14">
          {/* Left */}
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Let's talk</Overline>
              <h2 className="font-heading font-black text-gf-text text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-tighter mt-6">
                Let's build
                <br />
                <span className="text-gf-primary">the future.</span>
              </h2>
              <p className="font-body text-gf-muted text-base leading-relaxed mt-8 max-w-md">
                Whether you're planning a solar-plus-storage asset, need transmission engineering or are
                structuring project debt — tell us about your mandate.
              </p>

              <div className="mt-12 space-y-5">
                <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 flex items-center justify-center border border-white/15 group-hover:border-gf-primary transition-colors">
                    <Mail className="w-5 h-5 text-gf-primary" />
                  </span>
                  <span className="font-body text-gf-text group-hover:text-gf-primary transition-colors">{EMAIL}</span>
                </a>
                <a href={`tel:${PHONE.replace(/\s/g, "")}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 flex items-center justify-center border border-white/15 group-hover:border-gf-primary transition-colors">
                    <Phone className="w-5 h-5 text-gf-primary" />
                  </span>
                  <span className="font-body text-gf-text group-hover:text-gf-primary transition-colors">{PHONE}</span>
                </a>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 flex items-center justify-center border border-white/15 group-hover:border-gf-primary transition-colors">
                    <MessageCircle className="w-5 h-5 text-gf-primary" />
                  </span>
                  <span className="font-body text-gf-text group-hover:text-gf-primary transition-colors">Chat on WhatsApp</span>
                </a>
                <a href="/verify-whatsapp" data-testid="contact-verify-whatsapp" className="font-mono text-[10px] uppercase tracking-wider text-gf-muted hover:text-gf-primary -mt-2 block">Only +91 84510 99371 is official · verify a number →</a>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                <div className="sm:col-span-1">
                  <label className="font-mono text-xs uppercase tracking-wider text-gf-muted">Name *</label>
                  <input data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div className="sm:col-span-1">
                  <label className="font-mono text-xs uppercase tracking-wider text-gf-muted">Email *</label>
                  <input data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className={inputCls} />
                </div>
                <div className="sm:col-span-1 mt-6">
                  <label className="font-mono text-xs uppercase tracking-wider text-gf-muted">Company</label>
                  <CompanyInput testid="input-company" value={form.company} email={form.email} onChange={(v) => setForm((f) => ({ ...f, company: v }))} placeholder="Organisation" className={inputCls} />
                </div>
                <div className="sm:col-span-1 mt-6">
                  <label className="font-mono text-xs uppercase tracking-wider text-gf-muted">Area of interest</label>
                  <select data-testid="input-interest" value={form.interest} onChange={update("interest")} className={`${inputCls} appearance-none cursor-pointer`}>
                    <option value="" className="bg-gf-surface">Select a service</option>
                    <optgroup label="Core services" className="bg-gf-surface">
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.title} className="bg-gf-surface">{s.title}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Other services" className="bg-gf-surface">
                      {OTHER_SERVICES.map((s) => (
                        <option key={s} value={s} className="bg-gf-surface">{s}</option>
                      ))}
                    </optgroup>
                    <option value="Other" className="bg-gf-surface">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2 mt-6">
                  <label className="font-mono text-xs uppercase tracking-wider text-gf-muted">Message *</label>
                  <textarea data-testid="input-message" value={form.message} onChange={update("message")} rows={4} placeholder="Tell us about your project or mandate" className={`${inputCls} resize-none`} />
                </div>
                <div className="sm:col-span-2 mt-8 flex flex-wrap items-center gap-4">
                  <OtpField email={form.email} onToken={setOtpToken} />
                  <button
                    type="submit"
                    data-testid="submit-inquiry"
                    disabled={loading}
                    className="group inline-flex items-center gap-2 bg-gf-primary text-gf-bg font-body font-bold text-base px-8 py-4 hover:bg-gf-primary-hover transition-colors duration-200 disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send inquiry <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></>}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>

        {/* Footer rendered separately via SiteFooter */}
      </div>
    </section>
  );
};
